import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './components/feedback';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth';
import { UserRole } from './types/user';
import './App.css';

// Import route configurations
import { getAllCompanyRoutes } from './routes/companyRoutes';
import { getAllConsultantRoutes } from './routes/consultantRoutes';
import { getAllAuditorRoutes } from './routes/auditorRoutes';
import { getAllAdminRoutes } from './routes/adminRoutes';
import { getAllPublicRoutes } from './routes/publicRoutes';

// Import unauthorized page
import { UnauthorizedPage } from './pages/public/UnauthorizedPage';

/**
 * Layout components for different user roles
 * These would wrap the routes and provide role-specific navigation
 */
const CompanyLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // TODO: Implement company-specific layout with sidebar navigation
  return <>{children}</>;
};

const ConsultantLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // TODO: Implement consultant-specific layout with sidebar navigation
  return <>{children}</>;
};

const AuditorLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // TODO: Implement auditor-specific layout with sidebar navigation
  return <>{children}</>;
};

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // TODO: Implement admin-specific layout with sidebar navigation
  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <ToastProvider position="top-right" maxToasts={5}>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            {getAllPublicRoutes().map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}

            {/* Company Routes - Protected */}
            <Route path="/company/*" element={
              <ProtectedRoute roles={[UserRole.COMPANY_ADMIN, UserRole.COMPANY_STAFF]}>
                <CompanyLayout>
                  <Routes>
                    {getAllCompanyRoutes().map((route) => (
                      <Route
                        key={route.path}
                        path={route.path.replace('/company', '')}
                        element={route.element}
                      />
                    ))}
                    {/* Redirect /company to dashboard */}
                    <Route path="/" element={<Navigate to="/company/dashboard" replace />} />
                  </Routes>
                </CompanyLayout>
              </ProtectedRoute>
            } />

            {/* Consultant Routes - Protected */}
            <Route path="/consultant/*" element={
              <ProtectedRoute roles={[UserRole.CONSULTANT, UserRole.CONSULTANT_ADMIN]}>
                <ConsultantLayout>
                  <Routes>
                    {getAllConsultantRoutes().map((route) => (
                      <Route
                        key={route.path}
                        path={route.path.replace('/consultant', '')}
                        element={route.element}
                      />
                    ))}
                    {/* Redirect /consultant to dashboard */}
                    <Route path="/" element={<Navigate to="/consultant/dashboard" replace />} />
                  </Routes>
                </ConsultantLayout>
              </ProtectedRoute>
            } />

            {/* Auditor Routes - Protected */}
            <Route path="/auditor/*" element={
              <ProtectedRoute roles={[UserRole.AUDITOR, UserRole.LEAD_AUDITOR]}>
                <AuditorLayout>
                  <Routes>
                    {getAllAuditorRoutes().map((route) => (
                      <Route
                        key={route.path}
                        path={route.path.replace('/auditor', '')}
                        element={route.element}
                      />
                    ))}
                    {/* Redirect /auditor to dashboard */}
                    <Route path="/" element={<Navigate to="/auditor/dashboard" replace />} />
                  </Routes>
                </AuditorLayout>
              </ProtectedRoute>
            } />

            {/* Admin Routes - Protected */}
            <Route path="/admin/*" element={
              <ProtectedRoute roles={[UserRole.CB_ADMIN, UserRole.SYSTEM_ADMIN]}>
                <AdminLayout>
                  <Routes>
                    {getAllAdminRoutes().map((route) => (
                      <Route
                        key={route.path}
                        path={route.path.replace('/admin', '')}
                        element={route.element}
                      />
                    ))}
                    {/* Redirect /admin to dashboard */}
                    <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
                  </Routes>
                </AdminLayout>
              </ProtectedRoute>
            } />

            {/* Unauthorized Page */}
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
